import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { usePriceData } from './usePriceData';
import priceWebSocket from '../services/priceWebSocket';

// Use environment variable for API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export function useLivePrice() {
  const [price, setPrice] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isBlinking, setIsBlinking] = useState(false);
  const { priceData, loading: loadingPriceData } = usePriceData();
  const blinkTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousPriceRef = useRef<number | null>(null);
  
  // Connect to WebSocket for live price updates
  useEffect(() => {
    // Set up WebSocket handlers
    const handlePriceUpdate = (newPrice: number) => {
      
      // Only blink if price has changed
      if (newPrice !== previousPriceRef.current) {
        // Clear any existing blink timeout
        if (blinkTimeoutRef.current) {
          clearTimeout(blinkTimeoutRef.current);
        }
        
        // Update price and start blinking
        setPrice(newPrice);
        previousPriceRef.current = newPrice;
        setIsBlinking(true);
        setLoading(false);
        
        // Stop blinking after delay
        blinkTimeoutRef.current = setTimeout(() => {
          setIsBlinking(false);
          blinkTimeoutRef.current = null;
        }, 800);
      }
    };
    
    // Register listeners and connect
    priceWebSocket.on('price', handlePriceUpdate);
    priceWebSocket.connect();
    
    // Cleanup on unmount
    return () => {
      priceWebSocket.off('price', handlePriceUpdate);
      if (blinkTimeoutRef.current) {
        clearTimeout(blinkTimeoutRef.current);
      }
    };
  }, []);
  
  // Initial price loading from historical data or API
  useEffect(() => {
    // Skip if we already have a live price from WebSocket
    if (price !== null) return;
    
    // Use the latest historical price point if available
    if (!loadingPriceData && priceData.length > 0) {
      const latestHistoricalPrice = priceData[priceData.length - 1].value;
      setPrice(latestHistoricalPrice);
      previousPriceRef.current = latestHistoricalPrice;
      setLoading(false);
      return;
    }
    
    // Fallback: If historical data isn't available, fetch once from live price API
    const fetchInitialPrice = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/live-price`);
        
        const newPrice = response.data.price;
        setPrice(newPrice);
        previousPriceRef.current = newPrice;
        setError(null);
      } catch (err) {
        console.error("Error fetching initial price:", err);
        setError(err instanceof Error ? err : new Error('Failed to fetch initial price'));
      } finally {
        setLoading(false);
      }
    };
    
    if (loading) {
      fetchInitialPrice();
    }
  }, [priceData, loadingPriceData, loading, price]);
  
  return { price, loading, error, isBlinking };
}
